<template>
  <div>
    <div
      :class="{
        'box-with-shadow': !inner && currentPageRows.length,
        'data-table-footered': footered,
        'data-table-container--no-data': ready && !currentPageRows.length
      }"
      class="data-table-container"
    >
      <table
        v-if="ready && filteredData.length"
        class="table is-marginless is-fullwidth data-table"
      >
        <TableHeader
          :namespace="name"
          :columns="sortingColumns"
          :allow-selection="allowSelection"
          :sorted-column-index="sortedColumnIndex"
          :all-rows-selected="allRowsSelected"
          @selected-all="(value) => allRowsSelected = value"
          @on-sort-change="sortBy"
        />
        <TableBody
          :rows-data="currentPageRows"
          :row-action="rowAction"
          :allow-selection="allowSelection"
          :all-rows-selected="allRowsSelected"
          :columns="sortingColumns"
        >
          <slot />
          <template #expanded-row="slotProps">
            <slot
              name="expanded-row"
              v-bind="slotProps"
            />
          </template>
        </TableBody>
        <tfoot v-if="$slots.footer">
          <slot
            name="footer"
            :footer-data="footerData"
            :columns="sortingColumns"
          />
        </tfoot>

        <TableFooter
          v-if="footered"
          :columns="sortingColumns"
          :footer-data="footerData"
          :title-colspan="footerOptions.colspan"
          :title="footerOptions.title"
        />
      </table>
      <div
        v-if="ready && !currentPageRows.length"
        class="columns is-mobile table-no-rows-area"
      >
        <span class="column is-12 no-elements">
          {{ $t('comps.DataTable.noRows') }}
        </span>
      </div>
    </div>
    <div
      v-if="ready && totalPages > 1"
      class="data-table-pagination"
    >
      <SingleButton
        :disabled="page === 1"
        appearance="primary"
        @click="page = page - 1"
      >
        {{ $t('comps.DataTable.pagination.prev') }}
      </SingleButton>
      <SingleButton
        :disabled="page === totalPages"
        class="has-margin-left-1"
        appearance="primary"
        @click="page = page + 1"
      >
        {{ $t('comps.DataTable.pagination.next') }}
      </SingleButton>
    </div>
  </div>
</template>

<script>
import { Fragment } from 'vue';
import SingleButton from '@/components/buttons/SingleButton.vue';
import comparator from '@/services/comparator';
import TableHeader from './TableHeader.vue';
import TableFooter from './TableFooter.vue';
import TableBody from './TableBody.vue';

// Vue 3: i vnode dello slot di default non hanno più `componentOptions.propsData`.
// Le props stanno in `vnode.props`, con le chiavi così come scritte nel template
// (kebab-case incluso). Vue 2 le normalizzava a camelCase: replichiamo qui.
const camelize = str => str.replace(/-(\w)/g, (_, c) => c.toUpperCase());

// Appiattisce eventuali Fragment / nodi testo, tenendo solo i vnode colonna.
const flattenColumns = (vnodes) => {
  const out = [];
  (vnodes || []).forEach((vnode) => {
    if (vnode.type === Fragment && Array.isArray(vnode.children)) {
      out.push(...flattenColumns(vnode.children));
    } else if (typeof vnode.type === 'object' || typeof vnode.type === 'function') {
      out.push(vnode);
    }
  });
  return out;
};

export default {
  name: 'DataTable',
  components: {
    TableHeader,
    TableBody,
    TableFooter,
    SingleButton,
  },
  props: {
    data: { type: Array, default: () => [] },
    // columns: { type: Array, default: () => [] },
    filterFunction: { type: Function, default: null },
    returnToFirstPage: { type: Boolean, default: false },
    rowAction: { type: Function, default: null },
    footerData: { type: Object, default: null },
    footerOptions: { type: Object, default: null },
    filters: { type: Object, default: null },
    filterKey: { type: String, default: '' },
    name: { type: String, required: true },
    ready: { type: Boolean, default: false },
    inner: { type: Boolean, default: false },
    allowSelection: { type: Boolean, default: false },
    selectedRows: { type: Number, default: 0 },
    defaultSort: {
      type: Object,
      default: null,
      validator: value => value && value.column && ['asc', 'desc'].includes(value.order),
    },
  },
  emits: ['change'],
  data() {
    return {
      allRowsSelected: false,
      sortedColumnIndex: -1,
      sortingColumns: [],
      page: 1,
      perPage: 100,
      searchedRowsCount: this.data.length,
    };
  },
  computed: {
    currentPageRows() {

      if (!this.filteredData || !this.filteredData.length) return [];
      return this.filteredData.filter((item, index) =>
        index >= (this.perPage * (this.page - 1)) && index < this.perPage * this.page);
    },
    totalPages() {
      if (!this.filteredData.length) return 0;
      const rest = this.filteredData.length % this.perPage;
      if (rest === 0) return this.filteredData.length / this.perPage;
      return ((this.filteredData.length - rest) / this.perPage) + 1;
    },
    footered() {
      return this.footerData && this.footerOptions && this.ready && this.filteredData.length;
    },
    filteredData() {
      const { sortedColumnIndex } = this;
      const order = (this.sortingColumns[sortedColumnIndex] &&
        this.sortingColumns[sortedColumnIndex].order) || 1;
      let data = this.filters && this.filterFunction
        ? this.filterFunction(this.data) : this.data;

      if (sortedColumnIndex >= 0) {
        const column = this.sortingColumns[sortedColumnIndex];
        data = [...data].sort((a, b) => {
          const aValue = this.parseValueToSort(column.type, a[column.name]);
          const bValue = this.parseValueToSort(column.type, b[column.name]);
          switch (column.type) {
            case 'date':
              return comparator.compareDates(aValue, bValue) * order;
            case 'number':
            case 'currency':
              return comparator.compareNumbers(aValue, bValue) * order;
            default: {
              if (aValue === bValue) return 0;
              return (aValue > bValue ? 1 : -1) * order;
            }
          }
        });
      }
      return data;
    },
  },
  watch: {
    // Emettiamo `change` da un watcher (post-render) invece che dentro la computed
    // `filteredData`: emettere durante il render mentre il parent muta `footerData`
    // (dipendenza dello stesso render) causava "Maximum recursive updates exceeded".
    filteredData: {
      handler(data) {
        this.$emit('change', data);
      },
      immediate: true,
    },
    selectedRows(newVal) {
      if (newVal === this.data.length) {
        this.allRowsSelected = true;
      } else if (newVal === 0) {
        this.allRowsSelected = false;
      }
    },
    returnToFirstPage(newVal) {
      if (newVal) {
        this.page = 1;

      }
    }
  },
  created() {
    // Le colonne sono passate come config statica via slot di default (pattern
    // ereditato da naviga-web). Le leggiamo una volta qui. In dev Vue avvisa
    // "Slot invoked outside of the render function" (no dependency tracking):
    // innocuo perché la config è statica, e l'avviso è rimosso nel build di produzione.
    const defaultSlot = flattenColumns(this.$slots.default ? this.$slots.default() : []);

    this.sortingColumns = defaultSlot.map((item, index) => {
      const rawProps = item.props || {};
      const column = {};
      Object.keys(rawProps).forEach((key) => {
        if (['key', 'ref', 'class', 'style'].includes(key)) return;
        column[camelize(key)] = rawProps[key];
      });
      const isDefaultSort = this.defaultSort && this.defaultSort.column === column.name;

      this.sortedColumnIndex = isDefaultSort ? index : this.sortedColumnIndex;
      return isDefaultSort ?
        { ...column, order: this.defaultSort.order === 'asc' ? 1 : -1 } : { ...column, order: 1 };
    });
  },
  methods: {
    sortBy(columnIndex) {
      this.page = 1;
      this.sortedColumnIndex = columnIndex;
      this.sortingColumns[columnIndex].order = this.sortingColumns[columnIndex].order * -1;
    },
    parseValueToSort(type, value) {
      switch (type) {
        case 'date':
          if (value instanceof Date) return value;
          else if (typeof value === 'string') return new Date(value);
          return value;
        case 'number':
        case 'currency':
          if (typeof value === 'number') return value;
          if (typeof value === 'string') return parseFloat(value);
          return value;
        default:
          return value;
      }
    },
  },
};
</script>

<style lang="scss">
.data-table-container {
  overflow-x: auto;
  white-space: nowrap;

  &.data-table-footered {
    padding-bottom: 0rem;
  }

  &--no-data {
    overflow: hidden;
  }

  // padding-bottom: 1.56rem;

  .data-table.table {
    background-color: transparent;
  }

  .table-no-rows-area {
    padding: 1rem 1rem;
    font-size: $size-9;
    font-weight: $weight-medium;
  }

}

.data-table-pagination {
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
}
</style>
